<template>
  <div class="widget w-equity-analytics-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header :tradeButtonsSymbol="ric" :wid="wid">
      <template #left>
        <c-incremental-search :ric="ric" @error="onSymbolSearchError" @change-ric="onSetRIC" />
        <c-refresh-button v-if="activeTab === 'timesAndSales'" @refresh="refresh" />
      </template>
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <c-widget-control-bar v-if="ric">
      <b-button-toolbar :aria-label="$t(`white-label.widgets.equityAnalytics.toolbar`)" class="d-flex mb-0" autofocus>
        <b-button-group class="d-flex">
          <v-button
            v-for="widget in widgets"
            :key="widget.id"
            :class="{
              'active-tab': activeTab === widget.id
            }"
            :aria-label="$t(`white-label.widgets.${widget.id}.widget-title`)"
            @click="setActiveTab(widget.id)"
            ref="toolbar"
            :pressed="activeTab === widget.id"
          >
            <font-awesome-icon :icon="['fas', widget.icon]" />
            <span class="widget-name ml-2">{{ $t(`white-label.widgets.${widget.id}.widget-title`) }}</span>
            <span class="sr-only">{{ $t(`white-label.widgets.${widget.id}.widget-title`) }}</span>
          </v-button>
        </b-button-group>
      </b-button-toolbar>
    </c-widget-control-bar>

    <div v-if="ric" class="widget-body" :class="activeWidgetClass">
      <Summary
        v-if="activeTab === 'summary'"
        class="border-0"
        :ric="ric"
        :fontSize="fontSize"
        :timeRange="summary.timeRange"
        :scaleType="summary.scaleType"
        :wid="wid"
        @updateTimeRange="onUpdateTimeRange"
        @updateScaleType="onUpdateScaleType"
      />
      <AnalystEvaluation v-else-if="activeTab === 'analystEvaluation'" wid="analystEvaluation#2" :ric="ric" />
      <div v-else-if="activeTab === 'tradeHistory'" class="trade-history-container">
        <TradeHistoryControlBar
          class="pt-1 border-0"
          :wid="wid"
          :controlDateFrom="tradeHistoryFromDate"
          :controlDateTo="tradeHistoryToDate"
          :controlInterval="tradeHistoryInterval"
          @dateFromChange="onUpdateTradeHistoryFromDate"
          @dateToChange="onUpdateTradeHistoryToDate"
          @intervalChange="onUpdateTradeHistoryInterval"
        />
        <TradeHistoryBody
          class="border-0"
          :wid="wid"
          :ric="ric"
          :controlDateFrom="tradeHistoryFromDate"
          :controlDateTo="tradeHistoryToDate"
          :controlInterval="tradeHistoryInterval"
          :sortDesc="tradeHistory.tableSort.sortDesc"
          :sortBy="tradeHistory.tableSort.sortBy"
          @sortChange="onUpdateTradeHistorySort"
        />
      </div>
      <TimesAndSales v-else-if="activeTab === 'timesAndSales'" ref="ts" :ric="ric" />
      <EstimatesRatios
        v-else-if="activeTab === 'estimatesRatios'"
        :fontSize="fontSize"
        :isFontBold="isFontBold"
        :locale="locale"
        :selection="$store.state.widget[wid].estimatesRatios.dataSelection"
        :ric="ric"
        :wid="wid"
        @selection-changed="onEstimatesRatiosSelectionChanged"
      />
      <component
        v-else
        v-bind:is="activeWidget()"
        class="border-0"
        :wid="wid"
        :ric="ric"
        :locale="locale"
        :fontSize="fontSize"
        :isFontBold="isFontBold"
      ></component>
    </div>
    <v-alert v-else align="center" class="m-2" show variant="light">{{ i18n('nosymbol', 'globalMessages') }}</v-alert>
    <b-toaster :name="notifierId"></b-toaster>
  </div>
</template>

<script>
import CIncrementalSearch from '@custom/CIncrementalSearch';
import CRefreshButton from '@custom/CRefreshButton';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';

import moment from 'moment';
import widgetCommons from '@mixins/widgetCommons';
import { toTimezoneDate } from '@/utils/date';
import { trading as tradingsWidgets, analysis as analysisWidgets } from '@/../widgets.json';

import Summary from '@widgets/WSummaryWidget/WBody';
import CompanyProfile from '@widgets/WCompanyProfileWidget/WBody';
import FinancialStatements from '@widgets/WFinancialStatementsWidget/WBody';
import EstimatesRatios from '@widgets/WEstimatesRatiosWidget/WBody';
import IndicesPerformanceCorrelation from '@widgets/WIndicesPerformanceCorrelationWidget/WBody';
import AnalystEvaluation from '@widgets/WAnalystEvaluationWidget/WBody';
import DividendsCapitalChanges from '@widgets/WDividendsCapitalChangesWidget/WBody';
import TradeHistoryControlBar from '@widgets/WTradeHistoryWidget/WControlBar';
import TradeHistoryBody from '@widgets/WTradeHistoryWidget/WBody';
import TimesAndSales from '@widgets/WTimesAndSalesWidget/WBody';

import { library as fontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import {
  faFileMedicalAlt as fasFileMedicalAlt,
  faUniversity as fasUniversity,
  faReceipt as fasReceipt,
  faChartBar as fasChartBar,
  faNetworkWired as fasNetworkWired,
  faAnalytics as fasAnalytics,
  faSackDollar as fasSackDollar,
  faSignal as fasSignal,
  faHistory as fasHistory,
  faCalendarCheck as fasCalendarCheck
} from '@fortawesome/pro-light-svg-icons';
fontAwesomeLibrary.add(
  fasFileMedicalAlt,
  fasUniversity,
  fasReceipt,
  fasChartBar,
  fasNetworkWired,
  fasAnalytics,
  fasSackDollar,
  fasSignal,
  fasHistory,
  fasCalendarCheck
);

export default {
  name: 'w-equity-analytics-widget',
  mixins: [widgetCommons],
  components: {
    CRefreshButton,
    CWidgetHeader,
    CWidgetControlBar,
    CIncrementalSearch,
    Summary,
    CompanyProfile,
    FinancialStatements,
    EstimatesRatios,
    IndicesPerformanceCorrelation,
    AnalystEvaluation,
    DividendsCapitalChanges,
    TradeHistoryControlBar,
    TradeHistoryBody,
    TimesAndSales
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      widgets: [
        {
          id: 'companyProfile',
          icon: analysisWidgets.items.companyProfile.icon.split('fa-')[1],
          class: 'company-profile'
        },
        {
          id: 'analystEvaluation',
          icon: analysisWidgets.items.analystEvaluation.icon.split('fa-')[1],
          class: 'analyst-evaluation'
        },
        {
          id: 'financialStatements',
          icon: analysisWidgets.items.financialStatements.icon.split('fa-')[1],
          class: 'financial-statements'
        },
        {
          id: 'estimatesRatios',
          icon: analysisWidgets.items.estimatesRatios.icon.split('fa-')[1],
          class: 'estimates-ratios'
        },
        {
          id: 'indicesPerformanceCorrelation',
          icon: analysisWidgets.items.indicesPerformanceCorrelation.icon.split('fa-')[1],
          class: 'indices-performance-correlation'
        },
        {
          id: 'dividendsCapitalChanges',
          icon: analysisWidgets.items.dividendsCapitalChanges.icon.split('fa-')[1],
          class: 'dividends-capital-changes'
        },
        {
          id: 'timesAndSales',
          icon: tradingsWidgets.items.timesAndSales.icon.split('fa-')[1],
          class: 'times-and-sales'
        },
        {
          id: 'tradeHistory',
          icon: tradingsWidgets.items.tradeHistory.icon.split('fa-')[1],
          class: 'trade-history'
        },
        {
          id: 'summary',
          icon: tradingsWidgets.items.summary.icon.split('fa-')[1],
          class: 'summary'
        }
      ]
    };
  },
  computed: {
    ric() {
      return this.$store.state.widget[this.wid].ric;
    },
    activeTab() {
      return this.$store.state.widget[this.wid].activeTab;
    },
    activeWidgetClass() {
      return `w-${this.widgets.find(w => w.id === this.activeTab).class}-widget`;
    },
    // summary
    summary() {
      return this.$store.state.widget[this.wid].summary;
    },
    // estimates & ratios
    locale() {
      return this.$store.state.applicationSettings.general.settings.applicationLanguage;
    },
    // trade history
    tradeHistoryFromDate() {
      return (
        this.$store.state.widget[this.wid].tradeHistory.fromDate ||
        moment(this.controlDateTo)
          .subtract(1, 'month')
          .toDate()
      );
    },
    tradeHistoryToDate() {
      return (
        this.$store.state.widget[this.wid].tradeHistory.toDate ||
        toTimezoneDate(this.$store.state.applicationSettings.general.settings.timezone)
      );
    },
    tradeHistoryInterval() {
      return this.$store.state.widget[this.wid].tradeHistory.interval;
    },
    tradeHistory() {
      return this.$store.state.widget[this.wid].tradeHistory;
    }
  },
  methods: {
    // Update Ric
    onSetRIC(value) {
      this.$store.commit(`widget/${this.wid}/RIC_CHANGED`, value || '');
      this.sendLinkUpdate(value);
    },
    setActiveTab(value) {
      this.$store.commit(`widget/${this.wid}/TAB_CHANGED`, value);
    },
    activeWidget() {
      return this.capitalize(this.activeTab);
    },
    onEstimatesRatiosSelectionChanged({ tableName, label }) {
      this.$store.commit(`widget/${this.wid}/ESTIMATES_RATIOS_SELECTION_CHANGED`, { key: tableName, value: label });
    },
    // summary
    onUpdateScaleType(value) {
      this.$store.commit(`widget/${this.wid}/SUMMARY_SCALE_TYPE_CHANGED`, value);
    },
    onUpdateTimeRange(value) {
      this.$store.commit(`widget/${this.wid}/SUMMARY_TIME_RANGE_CHANGED`, value);
    },
    // trade history
    onUpdateTradeHistoryFromDate(date) {
      this.$store.commit(`widget/${this.wid}/TRADE_HISTORY_FROM_DATE_CHANGED`, date);
    },
    onUpdateTradeHistoryToDate(date) {
      this.$store.commit(`widget/${this.wid}/TRADE_HISTORY_TO_DATE_CHANGED`, date);
    },
    onUpdateTradeHistoryInterval(value) {
      this.$store.commit(`widget/${this.wid}/TRADE_HISTORY_INTERVAL_CHANGED`, value);
    },
    // times and sales
    refresh() {
      this.$refs.ts.update();
    },
    capitalize(term) {
      return `${term.charAt(0).toUpperCase()}${term.substring(1, term.length)}`;
    },
    onUpdateTradeHistorySort(val) {
      this.$store.commit(`widget/${this.wid}/TRADE_HISTORY_SORT_COLUMN_CHANGED`, val.sortBy);
      this.$store.commit(`widget/${this.wid}/TRADE_HISTORY_SORT_ORDER_CHANGED`, val.sortDesc);
    }
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/equity-analytics-widget';
@import '~@/styles/widgets/summary-widget';
@import '~@/styles/widgets/company-profile-widget';
@import '~@/styles/widgets/financial-statements-widget';
@import '~@/styles/widgets/estimates-ratios-widget';
@import '~@/styles/widgets/indices-performance-correlation-widget';
@import '~@/styles/widgets/analyst-evaluation-widget';
@import '~@/styles/widgets/dividends-capital-changes-widget';
@import '~@/styles/widgets/trade-history-widget';
@import '~@/styles/widgets/times-and-sales-widget';
</style>
